import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from '../../../../../util/reactIntl';
import { stringify } from '../../../../../util/urlHelpers';
import {
  Heading,
  ListingCard,
  NamedLink,
  VerifiedCreatorCardSkeleton,
} from '../../../../../components';

import css from './ExploreVerifiedCreators.module.css';
import IconCollection from '../../../../../components/IconCollection/IconCollection';

const SELLER_PAGE_SIZE = 8;
const SEARCH_DEBOUNCE_MS = 300;

const ExploreVerifiedCreators = props => {
  const { landingSellers = {} } = props;
  const {
    sellerCards = [],
    categoryOptions = [],
    searchInProgress = false,
    searchError = null,
    onSearch,
  } = landingSellers;
  const intl = useIntl();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const initialSearchSkippedRef = useRef(false);
  const visibleSellerCards = sellerCards.slice(0, SELLER_PAGE_SIZE);
  const hasCards = visibleSellerCards.length > 0;
  const showSkeletons = searchInProgress && !hasCards;
  const showNoResults = !searchInProgress && !searchError && !hasCards;
  const searchParams = useMemo(() => {
    const keywords = query.trim();
    return {
      ...(keywords ? { keywords } : {}),
      ...(category ? { pub_seller_category: category } : {}),
    };
  }, [category, query]);
  const searchString = stringify(searchParams);

  useEffect(() => {
    if (!initialSearchSkippedRef.current) {
      initialSearchSkippedRef.current = true;
      return undefined;
    }

    if (typeof onSearch !== 'function') {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      const keywords = query.trim();
      onSearch({
        ...(keywords ? { keywords } : {}),
        ...(category ? { sellerCategory: category } : {}),
        page: 1,
        perPage: SELLER_PAGE_SIZE,
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [category, onSearch, query]);

  return (
    <section
      className={css.root}
      aria-label={intl.formatMessage({ id: 'ExploreVerifiedCreators.title' })}
    >
      <div className={css.inner}>
        <div className={css.headerRow}>
          <div className={css.headerText}>
            <Heading as="h2" styledAs="h2" rootClassName={css.title}>
              {intl.formatMessage({ id: 'ExploreVerifiedCreators.title' })}
            </Heading>
            <p className={css.subtitle}>
              {intl.formatMessage({ id: 'ExploreVerifiedCreators.subtitle' })}
            </p>
          </div>

          <div className={css.controls}>
            <div className={css.searchControl}>
              <span className={css.searchIcon} aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M16.5 16.5 21 21"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <input
                className={css.searchInput}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={intl.formatMessage({
                  id: 'ExploreVerifiedCreators.searchPlaceholder',
                })}
                aria-label={intl.formatMessage({
                  id: 'ExploreVerifiedCreators.searchPlaceholder',
                })}
              />
            </div>

            <select
              className={css.categorySelect}
              value={category}
              onChange={e => setCategory(e.target.value)}
              aria-label={intl.formatMessage({ id: 'ExploreVerifiedCreators.categoryLabel' })}
            >
              <option value="">
                {intl.formatMessage({ id: 'ExploreVerifiedCreators.allCategories' })}
              </option>
              {categoryOptions.map(c => (
                <option key={c.option} value={c.option}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={css.grid} role="list">
          {visibleSellerCards.map(c => (
            <ListingCard
              key={c.id}
              className={css.card}
              variant="verifiedCreatorCollection"
              thumbnailImage={c.profileImage}
              thumbnailAlt={c.name}
              itemsCountText={c.itemsCountText}
              categoryLabel={c.category}
              creatorName={c.name}
              creatorSubtitle={c.subtitle}
              ctaText={intl.formatMessage({ id: 'ExploreVerifiedCreators.cta' })}
              ctaHref={c.profileHref}
              intl={intl}
            />
          ))}
          {showSkeletons
            ? Array.from({ length: SELLER_PAGE_SIZE }, (_, index) => (
                <VerifiedCreatorCardSkeleton
                  className={css.card}
                  key={`landing-seller-skeleton-${index}`}
                />
              ))
            : null}
        </div>
        <span className={css.loadingStatus} role="status" aria-live="polite">
          {searchInProgress && hasCards
            ? intl.formatMessage({ id: 'ExploreVerifiedCreators.loading' })
            : null}
        </span>
        {searchError ? (
          <p className={css.feedback}>
            {intl.formatMessage({ id: 'ExploreVerifiedCreators.error' })}
          </p>
        ) : null}
        {showNoResults ? (
          <p className={css.feedback}>
            {intl.formatMessage({ id: 'ExploreVerifiedCreators.noResults' })}
          </p>
        ) : null}

        <div className={css.footerLinkRow}>
          <NamedLink
            name="SearchPage"
            className={css.footerLink}
            to={{ search: searchString ? `?${searchString}` : '' }}
          >
            {intl.formatMessage({ id: 'ExploreVerifiedCreators.exploreAll' })}{' '}
            <span aria-hidden="true">
              <IconCollection name="arrowRight" />
            </span>
          </NamedLink>
        </div>
      </div>
    </section>
  );
};

export default ExploreVerifiedCreators;
